import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SignatureCanvas from 'react-signature-canvas'
import TableFieldPreview from '../../../../components/formBuilder/editForm/TableFieldPreview'
import {
  alignmentClass,
  isButtonRow,
  isStandaloneButton,
  resolveButtonAction,
  type ButtonConfig
} from '../../../../components/formBuilder/editForm/buttonFieldUtils'
import {
  getTableDimensions,
  isTableValueEmpty,
  parseTableCells,
  stringifyTableCells,
  resolvePrefillEditFieldId
} from '../../../../components/formBuilder/editForm/tableFieldUtils'
import PreviewPrefillLabelExtras from './PreviewPrefillLabelExtras'
import RecipientNameLabelIcon from '../../../../components/formBuilder/editForm/RecipientNameLabelIcon'
import PreviewPrefillConditionalEditSection from './PreviewPrefillConditionalEditSection'
import {
  preparePreviewPages,
  PrefillPreviewEntry,
  getConditionalPrefillActivationUpdates,
  isConfiguredForConditionalPrefillEdit,
  validateConditionalPrefillEdits
} from './prefillPreviewUtils'
import './ReadOnlyFormPreview.scss'
import './InteractiveFormPreview.scss'

interface Props {
  formData: any
  cardStyle?: string
  cardBackground?: string
  prefillEntries?: PrefillPreviewEntry[]
  isPrefillForm?: boolean
  initialPageIndex?: number
  onPageChange?: (pageIndex: number) => void
}

const NON_INPUT_FIELD_TYPES = new Set([
  'heading',
  'subheading',
  'banner',
  'formLogo',
  'divider',
  'button',
  'prevPageButton',
  'nextPageButton',
  'addPage',
  'subform',
  'buttonRow'
])

const clonePages = (pages: any[][]) => JSON.parse(JSON.stringify(pages)) as any[][]

const isRequired = (element: any) => !!element?.validation?.[0]?.rule?.includes?.('required')

const FieldLabel = ({ element, children }: { element: any; children?: React.ReactNode }) => {
  if (element.hideLabel) return null

  return (
    <label
      className={`readonly-form-preview__label ${
        isRequired(element) ? 'readonly-form-preview__label--required' : ''
      }`}
      style={{ color: element.color || undefined }}
    >
      {element.label}
      <RecipientNameLabelIcon element={element} />
      {children}
    </label>
  )
}

const PreviewCaptcha = ({
  element,
  value,
  onChange,
  onPassChange
}: {
  element: any
  value: string
  onChange: (value: string) => void
  onPassChange: (passed: boolean) => void
}) => {
  const [captcha, setCaptcha] = useState('')

  const generateCaptcha = () =>
    Array.from(window.crypto.getRandomValues(new Uint8Array(6)), (byte) =>
      ('0' + byte.toString(36)).slice(-1)
    )
      .join('')
      .toUpperCase()

  useEffect(() => {
    const next = generateCaptcha()
    setCaptcha(next)
    onPassChange(false)
  }, [])

  useEffect(() => {
    onPassChange(value.trim().toUpperCase() === captcha)
  }, [captcha, onPassChange, value])

  return (
    <div>
      <FieldLabel element={element} />
      <div className='readonly-form-preview__captcha'>
        <div className='readonly-form-preview__captcha-box'>{captcha}</div>
        <button
          type='button'
          className='btn btn-sm btn-outline-secondary'
          onClick={() => {
            setCaptcha(generateCaptcha())
            onChange('')
          }}
        >
          <i className='bi bi-arrow-clockwise' />
        </button>
        <input
          type='text'
          className='readonly-form-preview__input'
          placeholder='Enter captcha'
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ maxWidth: 180 }}
        />
      </div>
    </div>
  )
}

const PreviewRatings = ({
  element,
  value,
  onChange
}: {
  element: any
  value: number
  onChange: (value: number) => void
}) => (
  <div>
    <FieldLabel element={element} />
    <div
      className='readonly-form-preview__ratings'
      style={{ justifyContent: element.alignment || 'left' }}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type='button'
          className={`readonly-form-preview__star ${
            star <= value ? 'readonly-form-preview__star--filled' : ''
          }`}
          style={star <= value ? { color: element.color || '#f59e0b' } : undefined}
          onClick={() => onChange(star)}
        >
          ★
        </button>
      ))}
    </div>
  </div>
)

const InteractiveFormPreview: React.FC<Props> = ({
  formData,
  cardStyle = '',
  cardBackground = '',
  prefillEntries,
  isPrefillForm = false,
  initialPageIndex = 0,
  onPageChange
}) => {
  const formRef = useRef<HTMLFormElement>(null)
  const initialPages = useMemo(
    () =>
      clonePages(
        preparePreviewPages(formData, {
          prefillEntries,
          isPrefillForm: isPrefillForm || !!prefillEntries?.length
        })
      ),
    [formData, isPrefillForm, prefillEntries]
  )

  const [allPagesData, setAllPagesData] = useState<any[][]>(initialPages)
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [captchaPassed, setCaptchaPassed] = useState(true)
  const [captchaInput, setCaptchaInput] = useState('')

  useEffect(() => {
    setAllPagesData(clonePages(initialPages))
    setCurrentPageIndex(0)
    setFormErrors({})
    setIsSubmitted(false)
    setCaptchaInput('')
    setCaptchaPassed(true)
  }, [initialPages])

  useEffect(() => {
    onPageChange?.(currentPageIndex)
  }, [currentPageIndex, onPageChange])

  useEffect(() => {
    setCurrentPageIndex(initialPageIndex)
  }, [initialPageIndex])

  const formElements = allPagesData[currentPageIndex] || []
  const backgroundColor = cardStyle || '#ffffff'

  const updateElementInAllPages = useCallback(
    (pageIndex: number, elementIndexInPage: number, updates: Record<string, unknown>) => {
      setAllPagesData((prev) =>
        prev.map((page, pIdx) =>
          pIdx === pageIndex
            ? page.map((el, elIdx) => (elIdx === elementIndexInPage ? { ...el, ...updates } : el))
            : page
        )
      )
    },
    []
  )

  const updateElementByFieldId = useCallback(
    (fieldId: number, updater: (element: any) => any) => {
      // Only update the matching field on the current page — never mutate other pages
      // that historically shared a duplicate fieldId (e.g. Address and Plain Input both 9).
      setAllPagesData((prev) =>
        prev.map((page, pageIndex) =>
          pageIndex === currentPageIndex
            ? page.map((el) => (Number(el.fieldId) === Number(fieldId) ? updater(el) : el))
            : page
        )
      )
    },
    [currentPageIndex]
  )

  const activateDirectPrefillEdit = (fieldId: number) => {
    updateElementByFieldId(fieldId, (element) => ({
      ...element,
      prefillLocked: false,
      ...(isConfiguredForConditionalPrefillEdit(element)
        ? { prefillConditionalEditActive: true }
        : {})
    }))
  }

  const prefillEditFieldHandler = (
    parentFieldId: number,
    editFieldId: number,
    value: string,
    editFieldIndex?: number
  ) => {
    updateElementByFieldId(parentFieldId, (element) => {
      if (!Array.isArray(element.prefillEditFields)) return element
      return {
        ...element,
        prefillEditFields: element.prefillEditFields.map((editField: any, index: number) => {
          const resolvedId = resolvePrefillEditFieldId(editField, index, element.fieldId)
          const isMatch =
            editFieldIndex !== undefined
              ? index === editFieldIndex
              : Number(resolvedId) === Number(editFieldId)
          return isMatch
            ? { ...editField, fieldId: resolvedId, value }
            : { ...editField, fieldId: resolvedId }
        })
      }
    })

    const errorKey = `edit_${parentFieldId}_${editFieldId}`
    setFormErrors((prev) => {
      if (!prev[errorKey]) return prev
      const next = { ...prev }
      delete next[errorKey]
      return next
    })
  }

  const textHandler = (value: string, indexInPage: number) => {
    const field = formElements[indexInPage]
    if (field?.prefillLocked) return
    updateElementInAllPages(currentPageIndex, indexInPage, {
      value,
      ...getConditionalPrefillActivationUpdates(field)
    })
  }

  const tableCellHandler = (
    indexInPage: number,
    rowIndex: number,
    colIndex: number,
    value: string
  ) => {
    const element = formElements[indexInPage]
    if (!element || element.prefillLocked) return

    const { rows, cols } = getTableDimensions(element)
    const cells = parseTableCells(element.value, rows, cols)
    cells[rowIndex][colIndex] = value
    updateElementInAllPages(currentPageIndex, indexInPage, {
      value: stringifyTableCells(cells)
    })
  }

  const handleSwitchToggle = (indexInPage: number) => {
    const currentElement = formElements[indexInPage]
    if (currentElement?.prefillLocked) return
    const newValue = !currentElement.checked
    updateElementInAllPages(currentPageIndex, indexInPage, {
      checked: newValue,
      value: newValue,
      ...getConditionalPrefillActivationUpdates(currentElement)
    })
  }

  const handleCheckboxToggle = (indexInPage: number, optionIndex: number) => {
    const currentElement = formElements[indexInPage]
    if (currentElement?.prefillLocked) return

    const updatedOptions = currentElement.options.map((option: any, optIdx: number) =>
      optIdx === optionIndex ? { ...option, selected: !option.selected } : option
    )
    const newValueString = updatedOptions
      .filter((opt: any) => opt.selected)
      .map((opt: any) => opt.text)
      .join(';')
    updateElementInAllPages(currentPageIndex, indexInPage, {
      options: updatedOptions,
      value: newValueString,
      ...getConditionalPrefillActivationUpdates(currentElement)
    })
  }

  const handleRadioToggle = (indexInPage: number, optionIndex: number) => {
    const currentElement = formElements[indexInPage]
    if (currentElement?.prefillLocked) return

    const newValue = currentElement.options[optionIndex].text
    const updatedOptions = currentElement.options.map((opt: any, idx: number) => ({
      ...opt,
      selected: idx === optionIndex
    }))
    updateElementInAllPages(currentPageIndex, indexInPage, {
      value: newValue,
      options: updatedOptions,
      ...getConditionalPrefillActivationUpdates(currentElement)
    })
  }

  const validatePages = (pages: any[][] = allPagesData) => {
    const errors: Record<string, string> = {}

    pages.forEach((pageElements) => {
      pageElements.forEach((field) => {
        if (
          NON_INPUT_FIELD_TYPES.has(field.type) ||
          field.type === 'captcha' ||
          field.type === 'ratings'
        ) {
          return
        }

        const fieldName =
          field.label && typeof field.label === 'string'
            ? field.label.toLowerCase()
            : `field_${field.fieldId}`
        const fieldValue = field.value
        const rule = field.validation?.[0]?.rule

        if (!rule) return

        if (rule.includes('required') && field.type === 'table' && isTableValueEmpty(fieldValue)) {
          errors[fieldName] = field.validation[0].errorMessage || 'This field is required'
        } else if (
          rule.includes('required') &&
          (fieldValue === undefined || String(fieldValue).trim().length < 1)
        ) {
          errors[fieldName] = field.validation[0].errorMessage || 'This field is required'
        }
      })
    })

    return errors
  }

  const runPreviewValidation = (pages: any[][]): boolean => {
    const conditionalValidation = validateConditionalPrefillEdits(pages)
    const validationErrors = {
      ...conditionalValidation.errors,
      ...validatePages(pages)
    }

    if (Object.keys(validationErrors).length === 0) {
      setFormErrors({})
      return false
    }

    if (conditionalValidation.hasErrors) {
      setAllPagesData((prev) =>
        prev.map((page) =>
          page.map((element) =>
            conditionalValidation.fieldsToActivate.includes(element.fieldId)
              ? { ...element, prefillConditionalEditActive: true }
              : element
          )
        )
      )
    }
    setFormErrors(validationErrors)
    window.setTimeout(() => {
      document
        .querySelector(
          '.prefill-conditional-edit-section .text-danger, .interactive-form-preview__error, .prefill-conditional-edit-section'
        )
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 100)
    return true
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const hasCaptcha = allPagesData.flat().some((field) => field.type === 'captcha')
    if (hasCaptcha && !captchaPassed) {
      setFormErrors({ captcha: 'Captcha does not match' })
      return
    }

    const pagesSnapshot = allPagesData.map((page, pageIndex) =>
      pageIndex === currentPageIndex ? formElements : page
    )
    if (runPreviewValidation(pagesSnapshot)) {
      return
    }

    setFormErrors({})
    setIsSubmitted(true)
  }

  const handleNextPage = () => {
    if (runPreviewValidation([formElements])) {
      return
    }
    if (currentPageIndex < allPagesData.length - 1) {
      setFormErrors({})
      setCurrentPageIndex((prev) => prev + 1)
    }
  }

  const handlePrevPage = () => {
    if (currentPageIndex > 0) {
      setFormErrors({})
      setCurrentPageIndex((prev) => prev - 1)
    }
  }

  const resetPreview = () => {
    setAllPagesData(clonePages(initialPages))
    setCurrentPageIndex(0)
    setFormErrors({})
    setIsSubmitted(false)
    setCaptchaInput('')
    setCaptchaPassed(true)
    formRef.current?.reset()
  }

  const executeButtonAction = (button: ButtonConfig) => {
    const action = resolveButtonAction(button)
    if (action === 'next') {
      handleNextPage()
      return
    }
    if (action === 'previous') {
      handlePrevPage()
      return
    }
    if (action === 'submit') {
      formRef.current?.requestSubmit()
      return
    }
    if (action === 'reset') {
      resetPreview()
    }
  }

  const renderFieldError = (element: any) => {
    const key = element.label?.toLowerCase()
    if (!key || !formErrors[key]) return null
    return (
      <div className='interactive-form-preview__error text-danger fw-semibold mt-1' role='alert'>
        {formErrors[key]}
      </div>
    )
  }

  const fields = formElements.filter((el) => el?.type && !isStandaloneButton(el))
  const navButtons = formElements.filter((el) => isStandaloneButton(el))

  if (isSubmitted) {
    return (
      <div className='interactive-form-preview interactive-form-preview--success readonly-form-preview'>
        <div className='readonly-form-preview__shell'>
          <div className='readonly-form-preview__card text-center py-5' style={{ backgroundColor }}>
            <i className='bi bi-check-circle-fill text-success interactive-form-preview__success-icon' />
            <h4 className='mt-3 mb-2'>Form submitted (preview)</h4>
            <p className='text-muted mb-4'>
              This preview mimics the live form experience. No data was saved to the database.
            </p>
            <button type='button' className='btn btn-primary' onClick={resetPreview}>
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (fields.length === 0 && navButtons.length === 0) {
    return (
      <div className='readonly-form-preview readonly-form-preview--empty'>
        <p className='mb-0'>No fields on this page.</p>
      </div>
    )
  }

  return (
    <div
      className='interactive-form-preview readonly-form-preview'
      style={
        cardBackground
          ? {
              backgroundImage: `url(${cardBackground})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat'
            }
          : undefined
      }
    >
      <div className='readonly-form-preview__shell'>
        <div className='readonly-form-preview__card' style={{ backgroundColor }}>
          {allPagesData.length > 1 && (
            <div className='interactive-form-preview__page-indicator'>
              Page {currentPageIndex + 1} of {allPagesData.length}
            </div>
          )}

          <form ref={formRef} onSubmit={handleSubmit} noValidate>
            {fields.map((element, index) => {
              if (element.type === 'banner') {
                return (
                  <div
                    key={element.fieldId ?? index}
                    className='readonly-form-preview__banner'
                    style={{ backgroundImage: `url(${element.bannerSrc || ''})` }}
                  />
                )
              }

              return (
                <div
                  key={`preview-p${currentPageIndex}-f${element.fieldId}-i${index}`}
                  className='readonly-form-preview__field'
                >
                  {element.type === 'heading' &&
                    (() => {
                      const HeadingTag = (element.size || 'h4') as keyof JSX.IntrinsicElements
                      return (
                        <HeadingTag
                          className='fw-bold mb-3'
                          style={{
                            color: element.color || '#111827',
                            textAlign: element.alignment
                          }}
                        >
                          {element.formHeading}
                        </HeadingTag>
                      )
                    })()}

                  {element.type === 'subheading' && (
                    <p
                      className='mb-3'
                      style={{
                        color: element.color || '#374151',
                        textAlign: element.alignment,
                        whiteSpace: 'pre-line'
                      }}
                    >
                      {element.formSubHeading}
                    </p>
                  )}

                  {element.type === 'formLogo' && (
                    <div
                      className={`d-flex justify-content-${
                        element.alignment === 'right' ? 'end' : element.alignment || 'center'
                      } mb-3`}
                    >
                      <img
                        src={element.imgSrc}
                        alt='Form logo'
                        style={{
                          ...(Number(element.height) > 0
                            ? { height: `${Number(element.height)}px` }
                            : {}),
                          ...(Number(element.width) > 0
                            ? { width: `${Number(element.width)}px` }
                            : { maxWidth: 180 }),
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  )}

                  {element.type === 'divider' && (
                    <hr
                      className='my-4'
                      style={{
                        borderColor: element.color || '#dee2e6',
                        borderWidth: `${element.thickness || 1}px`
                      }}
                    />
                  )}

                  {['text', 'number', 'email', 'tel', 'url', 'password'].includes(element.type) && (
                    <>
                      <FieldLabel element={element}>
                        <PreviewPrefillLabelExtras
                          element={element}
                          onDirectEdit={activateDirectPrefillEdit}
                        />
                      </FieldLabel>
                      <input
                        type={
                          ['email', 'tel', 'url', 'password', 'number'].includes(element.type)
                            ? element.type
                            : 'text'
                        }
                        className='readonly-form-preview__input'
                        placeholder={element.placeholder}
                        value={element.value || ''}
                        readOnly={element.prefillLocked}
                        disabled={element.prefillLocked}
                        onChange={(e) => textHandler(e.target.value, index)}
                      />
                      {renderFieldError(element)}
                    </>
                  )}

                  {element.type === 'textarea' && (
                    <>
                      <FieldLabel element={element}>
                        <PreviewPrefillLabelExtras
                          element={element}
                          onDirectEdit={activateDirectPrefillEdit}
                        />
                      </FieldLabel>
                      <textarea
                        className='readonly-form-preview__textarea'
                        rows={element.rows || 4}
                        placeholder={element.placeholder}
                        value={element.value || ''}
                        readOnly={element.prefillLocked}
                        disabled={element.prefillLocked}
                        onChange={(e) => textHandler(e.target.value, index)}
                      />
                      {renderFieldError(element)}
                    </>
                  )}

                  {element.type === 'table' && (
                    <>
                      <FieldLabel element={element}>
                        <PreviewPrefillLabelExtras
                          element={element}
                          onDirectEdit={activateDirectPrefillEdit}
                        />
                      </FieldLabel>
                      <TableFieldPreview
                        element={element}
                        interactive={!element.prefillLocked}
                        onCellChange={(rowIndex, colIndex, value) =>
                          tableCellHandler(index, rowIndex, colIndex, value)
                        }
                      />
                      {renderFieldError(element)}
                    </>
                  )}

                  {element.type === 'select' && (
                    <>
                      <FieldLabel element={element}>
                        <PreviewPrefillLabelExtras
                          element={element}
                          onDirectEdit={activateDirectPrefillEdit}
                        />
                      </FieldLabel>
                      <select
                        className='readonly-form-preview__select'
                        value={element.value || ''}
                        disabled={element.prefillLocked}
                        onChange={(e) => textHandler(e.target.value, index)}
                      >
                        <option value=''>Select an option</option>
                        {(element.options || []).map((option: any, optionIndex: number) => (
                          <option key={optionIndex} value={option.text}>
                            {option.text}
                          </option>
                        ))}
                      </select>
                      {renderFieldError(element)}
                    </>
                  )}

                  {element.type === 'checkbox' && (
                    <>
                      <FieldLabel element={element}>
                        <PreviewPrefillLabelExtras
                          element={element}
                          onDirectEdit={activateDirectPrefillEdit}
                        />
                      </FieldLabel>
                      {(element.options || []).map((option: any, optionIndex: number) => (
                        <div key={optionIndex} className='readonly-form-preview__choice'>
                          <input
                            type='checkbox'
                            className='form-check-input mt-0'
                            id={`preview-cb-${element.fieldId}-${optionIndex}`}
                            checked={option.selected || false}
                            disabled={element.prefillLocked}
                            onChange={() => handleCheckboxToggle(index, optionIndex)}
                          />
                          <label
                            className='form-check-label'
                            htmlFor={`preview-cb-${element.fieldId}-${optionIndex}`}
                          >
                            {option.text}
                          </label>
                        </div>
                      ))}
                      {renderFieldError(element)}
                    </>
                  )}

                  {element.type === 'radio' && (
                    <>
                      <FieldLabel element={element}>
                        <PreviewPrefillLabelExtras
                          element={element}
                          onDirectEdit={activateDirectPrefillEdit}
                        />
                      </FieldLabel>
                      {(element.options || []).map((option: any, optionIndex: number) => (
                        <div key={optionIndex} className='readonly-form-preview__choice'>
                          <input
                            type='radio'
                            className='form-check-input mt-0'
                            name={`preview-radio-${element.fieldId}`}
                            checked={element.value === option.text || option.selected}
                            disabled={element.prefillLocked}
                            onChange={() => handleRadioToggle(index, optionIndex)}
                          />
                          <label className='form-check-label'>{option.text}</label>
                        </div>
                      ))}
                      {renderFieldError(element)}
                    </>
                  )}

                  {element.type === 'switch' && (
                    <>
                      <FieldLabel element={element}>
                        <PreviewPrefillLabelExtras
                          element={element}
                          onDirectEdit={activateDirectPrefillEdit}
                        />
                      </FieldLabel>
                      <div className='readonly-form-preview__choice'>
                        <div className='form-check form-switch mb-0'>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            checked={!!element.value}
                            disabled={element.prefillLocked}
                            onChange={() => handleSwitchToggle(index)}
                          />
                        </div>
                      </div>
                      {renderFieldError(element)}
                    </>
                  )}

                  {['date', 'datetime', 'time'].includes(element.type) && (
                    <>
                      <FieldLabel element={element}>
                        <PreviewPrefillLabelExtras
                          element={element}
                          onDirectEdit={activateDirectPrefillEdit}
                        />
                      </FieldLabel>
                      <input
                        type={element.type === 'datetime' ? 'datetime-local' : element.type}
                        className='readonly-form-preview__input'
                        value={element.value || ''}
                        disabled={element.prefillLocked}
                        onChange={(e) => textHandler(e.target.value, index)}
                      />
                      {renderFieldError(element)}
                    </>
                  )}

                  {element.type === 'daterange' && (
                    <>
                      <FieldLabel element={element}>
                        <PreviewPrefillLabelExtras
                          element={element}
                          onDirectEdit={activateDirectPrefillEdit}
                        />
                      </FieldLabel>
                      <div className='readonly-form-preview__daterange'>
                        <div>
                          <span className='readonly-form-preview__daterange-label'>Start date</span>
                          <input
                            type='date'
                            className='readonly-form-preview__input'
                            value={element.value?.split(' to ')?.[0] || ''}
                            disabled={element.prefillLocked}
                            onChange={(e) => {
                              const end = element.value?.split(' to ')?.[1] || ''
                              textHandler(`${e.target.value} to ${end}`.trim(), index)
                            }}
                          />
                        </div>
                        <span className='readonly-form-preview__daterange-sep'>–</span>
                        <div>
                          <span className='readonly-form-preview__daterange-label'>End date</span>
                          <input
                            type='date'
                            className='readonly-form-preview__input'
                            value={element.value?.split(' to ')?.[1] || ''}
                            disabled={element.prefillLocked}
                            onChange={(e) => {
                              const start = element.value?.split(' to ')?.[0] || ''
                              textHandler(`${start} to ${e.target.value}`.trim(), index)
                            }}
                          />
                        </div>
                      </div>
                      {renderFieldError(element)}
                    </>
                  )}

                  {element.type === 'file' && (
                    <>
                      <FieldLabel element={element}>
                        <PreviewPrefillLabelExtras
                          element={element}
                          onDirectEdit={activateDirectPrefillEdit}
                        />
                      </FieldLabel>
                      <input
                        type='file'
                        className='readonly-form-preview__input'
                        disabled={element.prefillLocked}
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          textHandler(file ? file.name : '', index)
                        }}
                      />
                      {renderFieldError(element)}
                    </>
                  )}

                  {element.type === 'signature' && (
                    <>
                      <FieldLabel element={element}>
                        <PreviewPrefillLabelExtras
                          element={element}
                          onDirectEdit={activateDirectPrefillEdit}
                        />
                      </FieldLabel>
                      <SignatureCanvas
                        penColor={element.color || '#000'}
                        backgroundColor={element.padColor || '#f5f8fa'}
                        canvasProps={{
                          width: Math.min(element.width || 500, 520),
                          height: element.height || 160,
                          className: 'sigCanvas border rounded'
                        }}
                      />
                    </>
                  )}

                  {element.type === 'ratings' && (
                    <PreviewRatings
                      element={element}
                      value={Number(element.value) || 0}
                      onChange={(value) => textHandler(String(value), index)}
                    />
                  )}

                  {element.type === 'captcha' && (
                    <>
                      <PreviewCaptcha
                        element={element}
                        value={captchaInput}
                        onChange={setCaptchaInput}
                        onPassChange={setCaptchaPassed}
                      />
                      {formErrors.captcha && (
                        <span className='interactive-form-preview__error'>
                          {formErrors.captcha}
                        </span>
                      )}
                    </>
                  )}

                  {element.type === 'buttonRow' && (
                    <>
                      <label className='readonly-form-preview__label text-muted'>
                        {element.label || 'Button row'}
                      </label>
                      <div
                        className={`d-flex flex-wrap gap-2 ${alignmentClass(element.alignment)}`}
                      >
                        {(element.buttons || []).map(
                          (button: ButtonConfig, buttonIndex: number) => {
                            const action = resolveButtonAction(button)
                            const disabled =
                              (action === 'previous' && currentPageIndex === 0) ||
                              (action === 'next' && currentPageIndex >= allPagesData.length - 1)

                            return (
                              <button
                                key={button.id || `row-btn-${element.fieldId}-${buttonIndex}`}
                                type='button'
                                className='readonly-form-preview__nav-btn'
                                style={{
                                  color: button.btnTxtColor || '#fff',
                                  backgroundColor: button.btnBgColor || '#4170e2'
                                }}
                                disabled={disabled}
                                onClick={() => executeButtonAction(button)}
                              >
                                {button.text}
                              </button>
                            )
                          }
                        )}
                      </div>
                    </>
                  )}

                  <PreviewPrefillConditionalEditSection
                    element={element}
                    formErrors={formErrors}
                    onEditFieldChange={prefillEditFieldHandler}
                  />
                </div>
              )
            })}

            <div className='readonly-form-preview__nav d-flex justify-content-between align-items-center gap-2 flex-wrap'>
              {allPagesData.length > 1 &&
                currentPageIndex > 0 &&
                !navButtons.some((element) => resolveButtonAction(element) === 'previous') && (
                  <button
                    type='button'
                    className='readonly-form-preview__nav-btn'
                    style={{ color: '#fff', backgroundColor: '#6c757d' }}
                    onClick={handlePrevPage}
                  >
                    Previous
                  </button>
                )}
              <div className='d-flex gap-2 flex-wrap ms-auto'>
                {navButtons.map((element, index) => {
                  const action = resolveButtonAction(element)
                  const disabled =
                    (action === 'previous' && currentPageIndex === 0) ||
                    (action === 'next' && currentPageIndex >= allPagesData.length - 1)

                  return (
                    <button
                      key={element.fieldId ?? index}
                      type={action === 'submit' ? 'submit' : 'button'}
                      className='readonly-form-preview__nav-btn'
                      style={{
                        color: element.btnTxtColor || '#fff',
                        backgroundColor: element.btnBgColor || '#4170e2'
                      }}
                      disabled={disabled}
                      onClick={() => {
                        if (action !== 'submit') {
                          executeButtonAction(element)
                        }
                      }}
                    >
                      {element.text}
                    </button>
                  )
                })}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default InteractiveFormPreview
